/**
 * Edit a campaign (name, daily / total budget) or a line item (name, bid amount, end date) in
 * place. X keeps objective, product, placements and start fixed.
 */
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { DateTimePicker } from '@/components/DateTimePicker';
import { FormField } from '@/components/FormField';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { fromMicros, isoToLocalInput, localDayStart, localInputToIso, toMicros } from '@/lib/x-ads/format';
import { errMsg, xAdsService, type XCampaign, type XLineItem } from '@/services/x-ads.service';

const amount = (micros?: number | null) => { const v = fromMicros(micros ?? null); return v === null ? '' : String(v); };

export function EditCampaign({ uid, campaign, currency, onClose, onSaved }: {
  uid: string; campaign: XCampaign | null; currency?: string; onClose: () => void; onSaved: (c: XCampaign) => void;
}) {
  const [name, setName] = useState('');
  const [daily, setDaily] = useState('');
  const [total, setTotal] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  useEffect(() => {
    if (!campaign) return;
    setName(campaign.name ?? ''); setDaily(amount(campaign.dailyBudgetLocalMicro)); setTotal(amount(campaign.totalBudgetLocalMicro)); setProblem(null);
  }, [campaign]);

  const save = async () => {
    if (!campaign) return;
    const d = toMicros(daily); const t = toMicros(total);
    if (!name.trim()) { setProblem('Give the campaign a name.'); return; }
    if (!d || d < 1) { setProblem('Enter a daily budget.'); return; }
    if (total && (!t || t < d)) { setProblem('The total budget must be at least the daily budget.'); return; }
    setSaving(true); setProblem(null);
    try {
      const saved = await xAdsService.updateCampaign(uid, campaign.id, { name: name.trim(), dailyBudgetMicros: d, ...(t ? { totalBudgetMicros: t } : {}) });
      toast.success('Campaign saved on X');
      onSaved({ ...campaign, ...saved });
    } catch (e) { setProblem(errMsg(e)); } finally { setSaving(false); }
  };
  const cur = currency ? ` (${currency})` : '';
  return <Modal open={!!campaign} onClose={onClose} title={`Edit campaign ${campaign?.id ?? ''}`}>
    <FormField label="Campaign name" htmlFor="xe-c-name" required><Input id="xe-c-name" maxLength={255} value={name} onChange={(e) => setName(e.target.value)} /></FormField>
    <div className="grid gap-x-4 sm:grid-cols-2">
      <FormField label={`Daily budget${cur}`} htmlFor="xe-c-daily" required><Input id="xe-c-daily" className="text-right tabular-nums" type="number" min="0" step="0.01" inputMode="decimal" value={daily} onChange={(e) => setDaily(e.target.value)} /></FormField>
      <FormField label={`Total budget${cur}`} htmlFor="xe-c-total" hint="Optional"><Input id="xe-c-total" className="text-right tabular-nums" type="number" min="0" step="0.01" inputMode="decimal" value={total} onChange={(e) => setTotal(e.target.value)} /></FormField>
    </div>
    {problem && <p role="alert" className="mb-3 text-sm text-danger">{problem}</p>}
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button onClick={save} loading={saving}>Save</Button>
    </div>
  </Modal>;
}

export function EditLineItem({ uid, lineItem, currency, onClose, onSaved }: {
  uid: string; lineItem: XLineItem | null; currency?: string; onClose: () => void; onSaved: (l: XLineItem) => void;
}) {
  const [name, setName] = useState('');
  const [bid, setBid] = useState('');
  const [end, setEnd] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const manualBid = !!lineItem?.bidStrategy && lineItem.bidStrategy !== 'AUTO';
  useEffect(() => {
    if (!lineItem) return;
    setName(lineItem.name ?? ''); setBid(amount(lineItem.bidAmountLocalMicro)); setEnd(isoToLocalInput(lineItem.endTime)); setProblem(null);
  }, [lineItem]);

  const save = async () => {
    if (!lineItem) return;
    if (!name.trim()) { setProblem('Give the line item a name.'); return; }
    const b = toMicros(bid);
    if (manualBid && !(b && b > 0)) { setProblem('Enter the bid amount.'); return; }
    const endIso = localInputToIso(end);
    if (endIso && lineItem.startTime && Date.parse(endIso) <= Date.parse(lineItem.startTime)) { setProblem('The end must be after the start.'); return; }
    setSaving(true); setProblem(null);
    try {
      const saved = await xAdsService.updateLineItem(uid, lineItem.id, { name: name.trim(), ...(manualBid && b ? { bidAmountMicros: b } : {}), endTime: endIso ?? '' });
      toast.success('Line item saved on X');
      onSaved({ ...lineItem, ...saved });
    } catch (e) { setProblem(errMsg(e)); } finally { setSaving(false); }
  };
  return <Modal open={!!lineItem} onClose={onClose} title={`Edit line item ${lineItem?.id ?? ''}`}>
    <FormField label="Line item name" htmlFor="xe-l-name" required><Input id="xe-l-name" maxLength={255} value={name} onChange={(e) => setName(e.target.value)} /></FormField>
    {manualBid && <FormField label={`Bid amount${currency ? ` (${currency})` : ''}`} htmlFor="xe-l-bid" required>
      <Input id="xe-l-bid" className="text-right tabular-nums" type="number" min="0" step="0.01" inputMode="decimal" value={bid} onChange={(e) => setBid(e.target.value)} />
    </FormField>}
    <FormField label="End" htmlFor="xe-l-end" hint="Your local time · empty = no end date">
      <DateTimePicker value={end} minDate={localDayStart()} onChange={setEnd} placeholder="No end date" />
    </FormField>
    <p className="xa-muted mb-3">Objective, product, placements and the start date stay as created — X does not change them.</p>
    {problem && <p role="alert" className="mb-3 text-sm text-danger">{problem}</p>}
    <div className="flex justify-end gap-2">
      <Button variant="secondary" onClick={onClose}>Cancel</Button>
      <Button onClick={save} loading={saving}>Save</Button>
    </div>
  </Modal>;
}
